import { ADS_DEFAULT_CAPS } from './caps';
/**
 * 분양광고 카드 문안 — 순수 함수(DB·시계·네트워크 없음).
 *  · 현장 소개(description, ≤60자) · 강조 태그(tags, 허용 목록에서 최대 2)
 *  · 소개를 비워 두면 카더라 현장 정보로 자동 문장: «사상구 엄궁동 · 1,670세대 · 선착순 분양 중»
 *    (값 없는 항목은 생략 · 숫자 사실만 · 과장 금지 · 가격·전화번호 없음)
 *  · 카드 머리줄(지역·현장명): 현장을 알면 «부산 사상구 · 현장명», 시군구 배너 대상 시군구면 «부산 연제구 · 광고주», 아니면 광고주명
 */

export const AD_DESC_MAX = 60;
export const AD_TAGS_MAX = 2;
/** 강조 태그 허용 목록(자유 입력 없음 — 과장 문구 방지). DB check 와 같은 목록. */
export const AD_TAG_OPTIONS = ['선착순', '잔여세대', '청약 임박', '견본주택 오픈', '역세권', '대단지', '즉시 입주', '계약금 할인'] as const;
export type AdTag = (typeof AD_TAG_OPTIONS)[number];

const TAG_SET = new Set<string>(AD_TAG_OPTIONS);
export const isAdTag = (t: unknown): t is AdTag => typeof t === 'string' && TAG_SET.has(t);

/** 저장·표시용 태그 정리 — 허용 목록만 · 중복 제거 · 최대 2. */
export function normalizeAdTags(v: unknown): AdTag[] {
  if (!Array.isArray(v)) return [];
  const out: AdTag[] = [];
  for (const x of v) {
    const t = typeof x === 'string' ? x.trim() : '';
    if (isAdTag(t) && !out.includes(t)) out.push(t);
    if (out.length >= AD_TAGS_MAX) break;
  }
  return out;
}

/** 입력 태그 검사 — 허용 밖·개수 초과를 그대로 알린다(정리하지 않음). */
export function tagIssues(v: unknown): string[] {
  if (v == null) return [];
  if (!Array.isArray(v)) return ['강조 태그 형식이 올바르지 않습니다.'];
  const out: string[] = [];
  const bad = v.filter((x) => !isAdTag(typeof x === 'string' ? x.trim() : x));
  if (bad.length) out.push(`고를 수 없는 강조 태그입니다: ${bad.map((b) => String(b)).join(', ').slice(0, 40)}`);
  if (new Set(v.map((x) => String(x).trim())).size > AD_TAGS_MAX) out.push(`강조 태그는 ${AD_TAGS_MAX}개까지 고를 수 있습니다.`);
  return out;
}

export const descLength = (s: string | null | undefined) => [...String(s ?? '').trim()].length;

/** 분양 단계 → 광고 소개용 짧은 상태(사실만). 모르는 단계는 null(생략). */
const SALE_PHASE: Record<string, string> = {
  unsold_active: '선착순 분양 중',
  subscription_open: '청약 진행 중',
  special_supply: '특별공급 진행 중',
  model_house_open: '견본주택 운영 중',
  pre_announcement: '분양 예정',
  site_planning: '분양 예정',
  award_pending: '당첨자 발표 대기',
  award_announced: '당첨자 발표',
  contract_signing: '계약 진행 중',
  contract: '계약 진행 중',
  pre_move_in: '입주 예정',
  move_in_ready: '입주 예정',
  move_in: '입주 중',
  move_in_started: '입주 중',
  union_established: '조합설립 단계',
  constructor_selected: '시공사 선정 단계',
  plan_approved: '사업시행인가 단계',
  mgmt_approved: '관리처분인가 단계',
  construction: '착공 단계',
};
export function salePhaseLabel(stage: string | null | undefined): string | null {
  return stage ? SALE_PHASE[stage] ?? null : null;
}

export interface AdSiteFacts {
  id: string;
  name: string | null;
  region: string | null;
  sigungu: string | null;
  dong?: string | null;
  complex_units?: number | null;
  total_units?: number | null;
  lifecycle_stage?: string | null;
}

const posInt = (n: unknown): number | null => (typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : null);

/** 자동 소개 문장 — «사상구 엄궁동 · 1,670세대 · 선착순 분양 중». 아무 값도 없으면 ''. */
export function autoAdIntro(site: AdSiteFacts | null | undefined): string {
  if (!site) return '';
  const place = [site.sigungu, site.dong].map((x) => String(x ?? '').trim()).filter(Boolean).join(' ');
  const units = posInt(site.complex_units) ?? posInt(site.total_units);
  const parts = [place, units ? `${units.toLocaleString('ko-KR')}세대` : '', salePhaseLabel(site.lifecycle_stage) ?? ''].filter(Boolean);
  return [...parts.join(' · ')].slice(0, AD_DESC_MAX).join('');
}

/** 화면에 낼 소개 — 광고주가 쓴 것(≤60) 우선, 비었으면 자동 문장. */
export function resolveAdIntro(description: string | null | undefined, site: AdSiteFacts | null | undefined): { text: string; auto: boolean } {
  const own = [...String(description ?? '').trim()].slice(0, AD_DESC_MAX).join('');
  if (own) return { text: own, auto: false };
  return { text: autoAdIntro(site), auto: true };
}

/** 카드 머리줄 — 지역·현장명. */
export function adMetaLine(a: { placement: string; targetKey: string; advertiserName: string; site?: AdSiteFacts | null }): string {
  const s = a.site;
  if (s && s.name) {
    const area = [s.region, s.sigungu].map((x) => String(x ?? '').trim()).filter(Boolean).join(' ');
    return area ? `${area} · ${s.name}` : s.name;
  }
  if (a.placement === 'district' && a.targetKey.includes(':')) {
    const [r, g] = a.targetKey.split(':');
    return `${r} ${g} · ${a.advertiserName}`;
  }
  return a.advertiserName;
}

// ── 홈 캐러셀 칸 규칙(결정적) ───────────────────────────────────────────────
export const HOME_MIN_CARDS = 3;
/** 홈 유료 카드 상한 — 정본 lib/ads/caps(= ADS_DEFAULTS.caps.home · 운영값은 app_config ads.caps.home). */
export const HOME_PAID_MAX = ADS_DEFAULT_CAPS.home;

/** 유료 n개일 때 «광고주 모집» 카드 수: 0~2 → 3칸까지 채움 · 3~5 → 1장(빈 자리 안내) · 6 → 0. */
export function homeRecruitCount(paid: number, max = HOME_PAID_MAX): number {
  const n = Math.max(0, Math.floor(paid || 0));
  if (n >= max) return 0;
  if (n < HOME_MIN_CARDS) return HOME_MIN_CARDS - n;
  return 1;
}

/** Fisher-Yates — rand 주입(테스트 결정성). 새 배열을 돌려준다. */
export function shuffled<T>(arr: readonly T[], rand: () => number = Math.random): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
